import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { brandPassportUrl, brandPassportDisplay } from "@/lib/passport";
import HealthScoreRing from "@/components/HealthScoreRing";
import AchievementVault from "@/components/AchievementVault";
import {
  ArrowRight, Globe, MapPin, ShieldCheck, BadgeCheck, Circle, Share2,
  Copy, ExternalLink, Crown, CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_LABELS = {
  muso_ai_claimed: "Muso.AI · Claimed",
  muso_ai_verified: "Muso.AI · Verified",
  allmusic_profile: "AllMusic",
  musicbrainz_listed: "MusicBrainz",
  genius_listed: "Genius",
  discogs_listed: "Discogs",
  jaxsta_listed: "Jaxsta",
  wikidata_listed: "Wikidata",
  imdb_listed: "IMDb",
  official_credits_page: "Official Credits Website",
  monitor_public_credits: "Credit Monitoring",
};

function BadgePill({ badge }) {
  const Icon = badge.tier === "verified" ? BadgeCheck : badge.tier === "claimed" ? ShieldCheck : Circle;
  return (
    <div data-testid={`passport-badge-${badge.tier}`}
      className="inline-flex items-center gap-2 px-3 py-1.5 border"
      style={{ borderColor: `${badge.color}66`, background: `${badge.color}14` }}>
      <Icon size={14} style={{ color: badge.color }} />
      <span className="text-[13px] uppercase tracking-widest font-semibold" style={{ color: badge.color }}>
        {badge.label}
      </span>
    </div>
  );
}

function ShareRow({ shareUrl, name }) {
  const text = `Check out ${name}'s BeatCredsi Passport — verified producer infrastructure.`;
  const enc = encodeURIComponent;
  const links = [
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}` },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}` },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { id: "instagram", label: "Instagram", href: `https://www.instagram.com/` },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Passport link copied.");
    } catch { toast.error("Could not copy."); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {links.map((l) => (
        <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer"
          data-testid={`share-${l.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 text-xs text-neutral-200 hover:border-amber-500 hover:text-amber-500 transition-colors">
          <Share2 size={12} /> {l.label}
        </a>
      ))}
      <button onClick={copy} data-testid="share-copy"
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-500/40 text-amber-500 text-xs hover:bg-amber-500/10 transition-colors">
        <Copy size={12} /> Copy Link
      </button>
    </div>
  );
}

export default function Passport() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const shareUrl = brandPassportUrl(username);
  const displayUrl = brandPassportDisplay(username);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/passport/${username}`);
        // 301-style redirect: handle was renamed, send the visitor to the new URL.
        if (data.redirected && data.canonical_username && data.canonical_username !== username.toLowerCase()) {
          navigate(`/c/${data.canonical_username}`, { replace: true });
          return;
        }
        setData(data);
        const canonical = data.canonical_username || username;
        const canonicalUrl = brandPassportUrl(canonical);
        const title = `${data.producer.name} | BeatCredsi Passport`;
        const desc = `View BeatCredsi Score, Credit Authority, Royalty Infrastructure, Verified Credits, and Achievement Vault.`;
        const image = data.producer.picture || "";
        document.title = title;

        const upsertMeta = (selector, attr, value, content) => {
          let m = document.head.querySelector(selector);
          if (!m) {
            m = document.createElement("meta");
            const [k, v] = attr.split("=");
            m.setAttribute(k, v.replace(/"/g, ""));
            document.head.appendChild(m);
          }
          m.setAttribute("content", content);
          return m;
        };

        upsertMeta('meta[name="description"]', 'name="description"', "content", desc);
        upsertMeta('meta[name="robots"]', 'name="robots"', "content", "index,follow,max-image-preview:large");
        upsertMeta('meta[property="og:title"]', 'property="og:title"', "content", title);
        upsertMeta('meta[property="og:description"]', 'property="og:description"', "content", desc);
        upsertMeta('meta[property="og:url"]', 'property="og:url"', "content", canonicalUrl);
        upsertMeta('meta[property="og:type"]', 'property="og:type"', "content", "profile");
        upsertMeta('meta[property="og:site_name"]', 'property="og:site_name"', "content", "BeatCredsi");
        upsertMeta('meta[name="twitter:card"]', 'name="twitter:card"', "content", "summary_large_image");
        upsertMeta('meta[name="twitter:title"]', 'name="twitter:title"', "content", title);
        upsertMeta('meta[name="twitter:description"]', 'name="twitter:description"', "content", desc);
        if (image) {
          upsertMeta('meta[property="og:image"]', 'property="og:image"', "content", image);
          upsertMeta('meta[name="twitter:image"]', 'name="twitter:image"', "content", image);
        }

        // Canonical link
        let canonicalEl = document.head.querySelector('link[rel="canonical"]');
        if (!canonicalEl) {
          canonicalEl = document.createElement("link");
          canonicalEl.setAttribute("rel", "canonical");
          document.head.appendChild(canonicalEl);
        }
        canonicalEl.setAttribute("href", canonicalUrl);
      } catch (err) {
        setError(err?.response?.status === 404 ? "Passport not found" : "Failed to load passport");
      }
    })();
  }, [username, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] p-6 text-center">
        <div className="text-overline mb-3">{error}</div>
        <h1 className="font-display text-4xl font-medium mb-4">No passport at <span className="text-amber-500">/c/{username}</span></h1>
        <p className="text-neutral-400 mb-8 max-w-md">This producer hasn't claimed their BeatCredsi Passport — or the URL is wrong.</p>
        <Link to="/signup" data-testid="cta-create-passport"
          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all">
          Claim Your Passport <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-overline">Loading passport…</div>
      </div>
    );
  }

  const p = data.producer;
  const status = data.status;
  const verifiedPlatforms = data.metadata_platforms.filter((mp) => mp.credit >= 1.0);
  const partialPlatforms = data.metadata_platforms.filter((mp) => mp.credit > 0 && mp.credit < 1.0);
  const royaltyPlatforms = data.royalty_platforms || [];
  const ROYALTY_LABELS = {
    pro_registration: "BMI / ASCAP / SESAC",
    soundexchange: "SoundExchange",
    mlc_registered: "The MLC",
    songtrust_registered: "Songtrust",
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Minimal public header */}
      <header className="px-6 md:px-10 lg:px-16 py-5 flex items-center justify-between border-b border-white/5">
        <Link to="/" className="font-display font-extrabold text-xl tracking-tight" data-testid="passport-brand">
          BeatCredsi<span className="text-amber-500 text-xs align-super">™</span>
        </Link>
        <Link to="/signup" data-testid="passport-header-cta"
          className="text-xs font-semibold uppercase tracking-widest px-4 py-2 bg-amber-500 text-black hover:brightness-110 transition-all">
          Get Your BeatCredsi Score
        </Link>
      </header>

      {/* PASSPORT CARD */}
      <main className="px-6 md:px-10 lg:px-16 py-12 max-w-6xl mx-auto">
        <div className="text-overline mb-3">BeatCredsi Passport<span className="text-amber-500">™</span></div>

        {/* Identity + Badge */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-start gap-6 mb-6">
              <div className="w-24 h-24 lg:w-32 lg:h-32 border border-amber-500/30 overflow-hidden shrink-0 bg-[#121212]">
                {p.picture ? (
                  <img src={p.picture} alt={p.name} className="w-full h-full object-cover" data-testid="passport-photo" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-amber-500 font-display text-4xl">
                    {p.name?.[0]?.toUpperCase() || "B"}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="font-display text-3xl lg:text-5xl font-medium tracking-tight" data-testid="passport-name">{p.name}</h1>
                  {p.founding_member && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-500/15 border border-amber-500/30">
                      <Crown size={12} className="text-amber-500" />
                      <span className="text-xs uppercase tracking-widest font-semibold text-amber-500">Founding #{p.founding_member_number}</span>
                    </span>
                  )}
                </div>
                <div className="font-mono-tech text-sm text-amber-500 mb-3" data-testid="passport-username">@{p.username}</div>
                <BadgePill badge={data.badge} />
                {p.bio && <p className="mt-4 text-neutral-300 leading-relaxed max-w-xl" data-testid="passport-bio">{p.bio}</p>}
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-neutral-400">
                  {p.location && (
                    <span className="inline-flex items-center gap-1.5" data-testid="passport-location">
                      <MapPin size={14} className="text-amber-500/70" /> {p.location}
                    </span>
                  )}
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid="passport-website"
                      className="inline-flex items-center gap-1.5 hover:text-amber-500 transition-colors">
                      <Globe size={14} className="text-amber-500/70" /> {p.website.replace(/^https?:\/\//, "")}
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 p-3 bg-[#0c0c0c] border border-amber-500/20 flex items-center gap-3" data-testid="passport-canonical-url">
              <ShieldCheck size={14} className="text-amber-500 shrink-0" />
              <span className="font-mono-tech text-xs text-amber-500 truncate flex-1">{displayUrl}</span>
            </div>

            <div className="mt-6">
              <div className="text-overline mb-3">Share Passport</div>
              <ShareRow shareUrl={shareUrl} name={p.name} />
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col items-center bg-[#0c0c0c] border border-white/5 p-8 lg:p-10">
            {data.has_assessment ? (
              <>
                <HealthScoreRing score={data.score} max={data.score_max} status={status} size={220} />
                <div className="mt-6 text-center">
                  <div className="text-overline mb-1">Risk Rating</div>
                  <div className="text-sm text-neutral-300">{data.risk_rating}</div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="text-overline mb-3">No Assessment Yet</div>
                <p className="text-neutral-400 text-sm max-w-xs">This producer hasn't completed the BeatCredsi assessment.</p>
              </div>
            )}
          </div>
        </div>

        {/* Metadata Authority + Verification */}
        {data.has_assessment && data.sub_scores?.metadata_authority && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5 border border-white/5 mb-12">
            <div className="lg:col-span-4 bg-[#0c0c0c] p-7" data-testid="passport-metadata-summary">
              <div className="text-overline mb-3">Metadata Authority</div>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-display text-5xl font-medium" style={{ color: data.sub_scores.metadata_authority.status?.color }}>
                  {data.sub_scores.metadata_authority.score}
                </span>
                <span className="font-mono-tech text-xs text-neutral-500">/ 1000</span>
              </div>
              <div className="text-overline" style={{ color: data.sub_scores.metadata_authority.status?.color }}>
                {data.sub_scores.metadata_authority.status?.label}
              </div>
              <p className="mt-4 text-sm text-neutral-400">
                {verifiedPlatforms.length} verified · {partialPlatforms.length} partial
              </p>
            </div>
            <div className="lg:col-span-8 bg-[#0c0c0c] p-7" data-testid="passport-verifications">
              <div className="text-overline mb-4">Verifications</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.metadata_platforms.map((mp) => (
                  <div key={mp.id} className="flex items-center gap-2 px-3 py-2 border border-white/5 bg-[#0A0A0A]">
                    {mp.credit >= 1.0 ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                      : mp.credit > 0 ? <AlertCircle size={14} className="text-amber-500 shrink-0" />
                      : <Circle size={14} className="text-neutral-700 shrink-0" />}
                    <span className={`text-sm truncate ${mp.credit >= 1.0 ? "text-white" : "text-neutral-500"}`}>
                      {PLATFORM_LABELS[mp.id] || mp.id}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Royalty Infrastructure Verifications */}
        {data.has_assessment && royaltyPlatforms.length > 0 && (
          <div className="mb-12" data-testid="passport-royalty-verifications">
            <div className="flex items-center justify-between mb-4">
              <div className="text-overline">Royalty Infrastructure Verified</div>
              <div className="font-mono-tech text-xs text-neutral-500">
                {royaltyPlatforms.filter((p) => p.credit >= 1.0).length} / {royaltyPlatforms.length} active
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {royaltyPlatforms.map((p) => (
                <div key={p.id} className="flex items-center gap-2 px-3 py-3 border border-white/5 bg-[#0c0c0c]">
                  {p.credit >= 1.0 ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                    : p.credit > 0 ? <AlertCircle size={14} className="text-amber-500 shrink-0" />
                    : <Circle size={14} className="text-neutral-700 shrink-0" />}
                  <span className={`text-sm truncate ${p.credit >= 1.0 ? "text-white" : "text-neutral-500"}`}>
                    {ROYALTY_LABELS[p.id] || p.id}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        {data.has_assessment && data.categories.length > 0 && (
          <div className="mb-12" data-testid="passport-categories">
            <div className="text-overline mb-4">Infrastructure Categories</div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5">
              {data.categories.map((c) => (
                <div key={c.id} className="bg-[#0c0c0c] p-5 flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{c.label}</div>
                  <div className="font-display text-3xl font-medium" style={{ color: c.status.color }}>{c.score}</div>
                  <div className="font-mono-tech text-xs text-neutral-500 mb-3">/ 1000</div>
                  <div className="h-1 w-full bg-white/5 mt-auto">
                    <div className="h-1" style={{ background: c.status.color, width: `${Math.max(2, (c.score / 1000) * 100)}%` }} />
                  </div>
                  <div className="text-[13px] uppercase tracking-widest font-semibold mt-2" style={{ color: c.status.color }}>{c.status.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Discography — proof lives on the credit platforms */}
        {(() => {
          const links = p.discography_links || {};
          const entries = [
            ["muso_ai_url", "Muso.AI", "#7C3AED"],
            ["genius_url", "Genius", "#FFFF64"],
            ["allmusic_url", "AllMusic", "#3B82F6"],
            ["discogs_url", "Discogs", "#000000"],
            ["jaxsta_url", "Jaxsta", "#22D3EE"],
            ["musicbrainz_url", "MusicBrainz", "#BA478F"],
            ["imdb_url", "IMDb", "#F5C518"],
            ["soundcloud_url", "SoundCloud", "#FF5500"],
            ["spotify_url", "Spotify", "#1DB954"],
          ].filter(([k]) => links[k]);
          if (!entries.length) return null;
          return (
            <div className="mb-12" data-testid="passport-discography">
              <div className="flex items-center justify-between mb-4">
                <div className="text-overline">Discography &amp; Credits</div>
                <div className="font-mono-tech text-xs text-neutral-500">Proof lives on the platforms</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {entries.map(([k, label, color]) => (
                  <a key={k} href={links[k]} target="_blank" rel="noopener noreferrer"
                    data-testid={`discog-link-${k}`}
                    className="group flex items-center justify-between gap-3 px-4 py-3 border border-white/10 bg-[#0c0c0c] hover:border-amber-500/40 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-2 h-2 shrink-0" style={{ background: color }} />
                      <div className="min-w-0">
                        <div className="text-sm text-white">View credits on {label}</div>
                        <div className="font-mono-tech text-xs text-neutral-500 truncate">{links[k].replace(/^https?:\/\//, "")}</div>
                      </div>
                    </div>
                    <ExternalLink size={14} className="text-neutral-500 group-hover:text-amber-500 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Achievement Vault */}
        {data.achievements && data.achievements.length > 0 && (
          <div className="mb-12">
            <AchievementVault achievements={data.achievements} isOwner={false} ownerName={p.name} />
          </div>
        )}

        {/* Growth CTA */}
        <div className="border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-8 lg:p-12 mb-12" data-testid="passport-growth-cta">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="text-overline mb-3">Measure Yours</div>
              <h2 className="font-display text-2xl lg:text-4xl font-medium tracking-tight">
                Every producer should have a <span className="text-amber-500">BeatCredsi Passport.</span>
              </h2>
              <p className="mt-3 text-sm lg:text-base text-neutral-300 leading-relaxed">
                22 questions. One score. Verified credibility the industry can look up.
              </p>
            </div>
            <Link to="/signup" data-testid="passport-cta-signup"
              className="inline-flex items-center gap-3 px-7 py-4 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all">
              Get Your BeatCredsi Score <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-white/5 pt-8 mt-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display font-extrabold text-lg">Powered by BeatCredsi<span className="text-amber-500 text-xs align-super">™</span></div>
            <div className="text-overline mt-1">Infrastructure Over Hype.<span className="text-amber-500">™</span></div>
          </div>
          <div className="text-xs text-neutral-500">© {new Date().getFullYear()} Only Few Get Selected, LLC</div>
        </footer>
      </main>
    </div>
  );
}
